/**
 * LlmProviderIndicator — shell-level badge showing the active LLM provider type.
 *
 * Renders in the TopStatusBar right section so users always know whether
 * AI requests will be processed locally (on-device) or remotely (data egress).
 *
 * - Lock  Local AI  — WebLLM is running on-device; no data leaves the browser
 * - Cloud Remote AI — an external API is configured (blue indicator); personal-remote requests trigger the consent gate
 * - Alert AI Error  — last request failed (auth, network, or WebGPU unavailable)
 * - (hidden)        — no provider configured yet
 *
 * Clicking the indicator navigates to Settings → AI section.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Cloud, AlertTriangle } from 'lucide-react';
import type { LlmManager, LlmLastError, WebLlmLoadingProgress } from './LlmManager';
import styles from './LlmProviderIndicator.module.css';

interface Props {
  llmManager: LlmManager;
}

export function LlmProviderIndicator({ llmManager }: Props) {
  const navigate = useNavigate();
  const provider = llmManager.getProvider();
  const [lastError, setLastError] = useState<LlmLastError | null>(() => llmManager.getLastError());
  const [loadingState, setLoadingState] = useState<WebLlmLoadingProgress | null>(
    () => llmManager.getWebLlmLoadingState(),
  );

  useEffect(() => llmManager.onErrorChange(setLastError), [llmManager]);
  useEffect(() => llmManager.onLoadingChange(setLoadingState), [llmManager]);

  if (!provider) return null;

  const isLocal = provider.type === 'local-webllm';
  const hasError = lastError !== null;
  const isLoading = isLocal && loadingState !== null;

  const errorMessages: Record<LlmLastError['code'], string> = {
    'auth-error': 'AI authentication failed — check your API key in Settings → AI.',
    'network-error': 'AI request failed — check your internet connection.',
    'webllm-unavailable': 'Local AI requires a browser with WebGPU support (Chrome 113+).',
    'provider-error': 'AI request failed — see Settings → AI to verify your provider.',
    'local-only': 'This feature uses content other people wrote and runs only on on-device AI.',
  };

  const title = hasError
    ? errorMessages[lastError.code]
    : (isLoading
      ? 'Local AI is loading — weights are cached in your browser.'
      : (isLocal
        ? 'AI is running on-device. Your data never leaves your browser.'
        : 'AI requests are sent to an external provider, only with your consent for each app.'));

  let label: string;
  if (hasError) {
    label = 'AI Error';
  } else if (isLoading) {
    label = loadingState!.loadProgress > 0 ? 'Loading AI' : 'Downloading';
  } else {
    label = isLocal ? 'Local AI' : 'Remote AI';
  }

  // Circular progress calculation around the icon (16x16 container, radius 6.5, circumference ~40.84)
  const progressFrac = isLoading
    ? (loadingState!.loadProgress > 0 ? loadingState!.loadProgress : loadingState!.downloadProgress)
    : 0;
  const ringRadius = 6.5;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const strokeDashoffset = ringCircumference * (1 - Math.min(1, Math.max(0, progressFrac)));
  const ringColor = 'var(--accent, #10b981)';

  let stateClass = styles.remote;
  let icon = <Cloud className={styles.icon} />;

  if (hasError) {
    stateClass = styles.error;
    icon = <AlertTriangle className={styles.icon} />;
  } else if (isLoading) {
    stateClass = styles.loading;
    icon = <Lock className={styles.iconLoading} />;
  } else if (isLocal) {
    stateClass = styles.local;
    icon = <Lock className={styles.icon} />;
  }

  return (
    <button
      type="button"
      className={`${styles.root} ${stateClass}`}
      title={`${title} — Click to open AI settings`}
      aria-label={`${label} — AI settings`}
      onClick={() => navigate('/settings?section=ai')}
    >
      <div className={styles.iconWrapper}>
        {isLoading && (
          <svg
            aria-hidden="true"
            className={styles.progressRing}
            viewBox="0 0 16 16"
          >
            {/* Background track circle */}
            <circle
              cx="8"
              cy="8"
              r={ringRadius}
              fill="none"
              stroke="color-mix(in srgb, currentColor 20%, transparent)"
              strokeWidth="1.75"
            />
            {/* Active progress arc */}
            <circle
              cx="8"
              cy="8"
              r={ringRadius}
              fill="none"
              stroke={ringColor}
              strokeWidth="1.75"
              strokeDasharray={ringCircumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.2s ease' }}
            />
          </svg>
        )}
        {icon}
      </div>
      <span className={styles.label}>{label}</span>
    </button>
  );
}
